import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  alpha,
} from '@mui/material';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
  ReferenceLine,
} from 'recharts';
import { SEARCE_COLORS } from '../../theme';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface LineConfig {
  dataKey: string;
  /** Display name used in legend and default tooltip */
  label: string;
  /** Static color for the line. Ignored when `getColor` is provided. */
  color?: string;
  /**
   * Value-based dynamic color.
   * Called with the point's value and the full data payload.
   * When set, dots and line segments are colored per-point.
   */
  getColor?: (value: number, payload: Record<string, any>) => string;
  strokeWidth?: number;
  strokeDasharray?: string;
  dotRadius?: number;
  activeDotRadius?: number;
  connectNulls?: boolean;
  showDot?: boolean;
  /** Per-point filter to selectively show/hide dots. Return `true` to show the dot. */
  shouldShowDot?: (payload: Record<string, any>, index: number) => boolean;
}

export interface ReferenceLineConfig {
  y?: number;
  x?: string;
  label?: string;
  color?: string;
  strokeDasharray?: string;
}

export interface TooltipField {
  dataKey: string;
  label: string;
  formatter?: (value: any, payload: Record<string, any>) => string;
  /** Optional per-field color. Falls back to corresponding line color. */
  color?: string;
}

export interface CustomLineChartProps {
  data: Record<string, any>[];
  lines: LineConfig[];

  // X-Axis
  xAxisDataKey: string;
  xAxisLabel?: string;
  xAxisAngle?: number;
  xAxisTickFormatter?: (value: any, index: number) => string;
  xAxisHeight?: number;
  /** Recharts interval prop – number, "preserveStart", "preserveEnd", "preserveStartEnd", or 0 for all ticks. */
  xAxisInterval?: number | 'preserveStart' | 'preserveEnd' | 'preserveStartEnd';

  // Y-Axis
  yAxisLabel?: string;
  yAxisDomain?: [number | string, number | string];
  yAxisTickFormatter?: (value: any, index: number) => string;
  yAxisWidth?: number;

  // Tooltip
  /** Fully custom tooltip renderer – receives recharts tooltip props. */
  renderTooltip?: (props: any) => React.ReactNode;
  /** Declarative tooltip fields. Used when `renderTooltip` is not provided. */
  tooltipFields?: TooltipField[];
  /**
   * Pin tooltip top edge at this Y offset (px, chart container coordinates).
   * Horizontal position still follows the active point. Use to avoid tooltips
   * jumping vertically or escaping below the plot when `allowEscapeViewBox.y` is true.
   */
  tooltipFixedY?: number;

  // Legend
  showLegend?: boolean;

  // Reference lines
  referenceLines?: ReferenceLineConfig[];

  // Chart dimensions
  height?: number;
  margin?: { top?: number; right?: number; bottom?: number; left?: number };

  // Empty state
  emptyMessage?: string;

  // Card wrapper
  title?: string;
  /** Title text color (defaults to primary body text). */
  titleColor?: string;
  /** Wrap chart in a Card. Defaults to `true`. */
  showCard?: boolean;

  /**
   * Additional recharts elements rendered inside the LineChart, before the
   * `lines`-driven `<Line>` components. Useful for injecting custom segment
   * Lines, extra reference lines, or any recharts-compatible children.
   */
  children?: React.ReactNode;
}

// ---------------------------------------------------------------------------
// Defaults
// ---------------------------------------------------------------------------

const DEFAULT_LINE_COLOR = SEARCE_COLORS.primary;
const DEFAULT_STROKE_WIDTH = 2;
const DEFAULT_DOT_RADIUS = 4;
const DEFAULT_ACTIVE_DOT_RADIUS = 6;
const DEFAULT_HEIGHT = 300;
const DEFAULT_Y_WIDTH = 40;
const DEFAULT_MARGIN = { top: 8, right: 20, left: 8, bottom: 5 };
const DEFAULT_EMPTY_MESSAGE = 'No data available';

// ---------------------------------------------------------------------------
// Internal: Default Tooltip
// ---------------------------------------------------------------------------

interface DefaultTooltipProps {
  active?: boolean;
  payload?: any[];
  label?: string;
  coordinate?: { x: number; y: number };
  viewBox?: { width: number; height: number };
  fields?: TooltipField[];
  lines: LineConfig[];
}

/**
 * Small horizontal nudge only (fraction of plot width). Left → nudge right; right → nudge left.
 */
function getTooltipHorizontalNudge(
  coordinate: { x: number; y: number } | undefined,
  viewBox: { width: number; height: number } | undefined,
): string {
  if (!coordinate || !viewBox?.width) return 'translateX(0)';
  const w = viewBox.width;
  const t = coordinate.x / w;
  if (t <= 0.22) return 'translateX(12px)';
  if (t >= 0.78) return 'translateX(-12px)';
  return 'translateX(0)';
}

const DefaultTooltip: React.FC<DefaultTooltipProps> = ({
  active,
  payload,
  label,
  coordinate,
  viewBox,
  fields,
  lines,
}) => {
  if (!active || !payload || payload.length === 0) return null;

  const horizontalNudge = getTooltipHorizontalNudge(coordinate, viewBox);
  const dataPayload = payload[0]?.payload ?? {};

  const lineColorMap: Record<string, string> = {};
  lines.forEach((l) => {
    const resolvedColor = l.getColor
      ? l.getColor(dataPayload[l.dataKey], dataPayload)
      : l.color ?? DEFAULT_LINE_COLOR;
    lineColorMap[l.dataKey] = resolvedColor;
  });

  const displayFields: { label: string; value: string; color: string }[] = [];

  if (fields && fields.length > 0) {
    fields.forEach((f) => {
      const rawValue = dataPayload[f.dataKey];
      const formatted = f.formatter
        ? f.formatter(rawValue, dataPayload)
        : rawValue != null
          ? rawValue.toLocaleString?.() ?? String(rawValue)
          : '–';
      displayFields.push({
        label: f.label,
        value: formatted,
        color: f.color ?? lineColorMap[f.dataKey] ?? SEARCE_COLORS.textPrimary,
      });
    });
  } else {
    payload.forEach((entry: any) => {
      if (entry.value == null) return;
      displayFields.push({
        label: entry.name ?? entry.dataKey,
        value: entry.value.toLocaleString?.() ?? String(entry.value),
        color: entry.color ?? SEARCE_COLORS.textPrimary,
      });
    });
  }

  return (
    <Box
      sx={{
        backgroundColor: SEARCE_COLORS.cardBackground,
        border: `1px solid ${SEARCE_COLORS.border}`,
        borderRadius: 2,
        p: 1.5,
        boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
        pointerEvents: 'none',
        minWidth: 180,
        maxWidth: 260,
        transform: horizontalNudge,
        marginLeft: 0,
        marginTop: 0,
      }}
    >
      <Typography
        variant="body2"
        sx={{
          fontWeight: 600,
          mb: 1,
          color: SEARCE_COLORS.textPrimary,
          borderBottom: `1px solid ${alpha(SEARCE_COLORS.border, 0.5)}`,
          pb: 0.5,
        }}
      >
        {label}
      </Typography>
      {displayFields.map((f, idx) => (
        <Box
          key={idx}
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 2,
            mb: 0.25,
          }}
        >
          <Typography variant="body2" sx={{ color: SEARCE_COLORS.textSecondary }}>
            {f.label}:
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 600, color: f.color }}>
            {f.value}
          </Typography>
        </Box>
      ))}
    </Box>
  );
};

// ---------------------------------------------------------------------------
// Internal: Dot renderers
// ---------------------------------------------------------------------------

const renderStaticDot = (
  color: string,
  radius: number,
  shouldShowDot?: (payload: Record<string, any>, index: number) => boolean,
) => (props: any) => {
  const { cx, cy, key, payload, index } = props;
  if (shouldShowDot && !shouldShowDot(payload, index)) {
    return <circle key={key} cx={cx} cy={cy} r={0} fill="transparent" />;
  }
  return <circle key={key} cx={cx} cy={cy} r={radius} fill={color} stroke={color} strokeWidth={2} />;
};

const renderDynamicDot = (
  dataKey: string,
  getColor: (value: number, payload: Record<string, any>) => string,
  radius: number,
  shouldShowDot?: (payload: Record<string, any>, index: number) => boolean,
) => (props: any) => {
  const { cx, cy, payload, key, index } = props;
  if (shouldShowDot && !shouldShowDot(payload, index)) {
    return <circle key={key} cx={cx} cy={cy} r={0} fill="transparent" />;
  }
  const value = payload?.[dataKey];
  const color = getColor(value, payload);
  return <circle key={key} cx={cx} cy={cy} r={radius} fill={color} stroke={color} strokeWidth={2} />;
};

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------

const CustomLineChart: React.FC<CustomLineChartProps> = ({
  data,
  lines,
  xAxisDataKey,
  xAxisLabel,
  xAxisAngle = 0,
  xAxisTickFormatter,
  xAxisHeight,
  xAxisInterval,
  yAxisLabel,
  yAxisDomain,
  yAxisTickFormatter,
  yAxisWidth = DEFAULT_Y_WIDTH,
  renderTooltip,
  tooltipFields,
  tooltipFixedY,
  showLegend = false,
  referenceLines,
  height = DEFAULT_HEIGHT,
  margin = DEFAULT_MARGIN,
  emptyMessage = DEFAULT_EMPTY_MESSAGE,
  title,
  titleColor = SEARCE_COLORS.textPrimary,
  showCard = true,
  children,
}) => {
  const resolvedXAxisHeight = xAxisHeight ?? (xAxisAngle !== 0 ? 60 : 30);
  const textAnchor = xAxisAngle < 0 ? 'end' : xAxisAngle > 0 ? 'start' : 'middle';

  // ---- Empty state ----
  if (!data || data.length === 0) {
    const emptyContent = (
      <Box sx={{ height, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Typography variant="body2" sx={{ color: SEARCE_COLORS.textSecondary }}>
          {emptyMessage}
        </Typography>
      </Box>
    );

    if (!showCard) return emptyContent;

    return (
      <Card
        sx={{
          borderRadius: 3,
          boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
          border: `1px solid ${alpha(SEARCE_COLORS.neutral_300, 0.5)}`,
          height: 'auto',
          width: '100%',
          alignSelf: 'flex-start',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <CardContent sx={{ p: 2, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', flex: '0 0 auto' }}>
          {title && (
            <Typography variant="h6" sx={{ fontWeight: 600, color: titleColor, mb: 2, fontSize: '1rem' }}>
              {title}
            </Typography>
          )}
          {emptyContent}
        </CardContent>
      </Card>
    );
  }

  /** Let the tooltip extend past the plot horizontally so it is not squeezed/clipped; Y still bounded. */
  const tooltipAllowEscape = { x: true as const, y: false as const };
  const tooltipPosition = tooltipFixedY != null ? { y: tooltipFixedY } : undefined;
  /** Negative offset pulls the tooltip above the active point (SVG y increases downward). */
  const tooltipOffset = tooltipFixedY != null ? 8 : -24;

  // ---- Tooltip content resolver ----
  const tooltipContent = renderTooltip ? (
    <RechartsTooltip
      content={renderTooltip}
      allowEscapeViewBox={tooltipAllowEscape}
      position={tooltipPosition}
      offset={tooltipOffset}
      wrapperStyle={{
        zIndex: 20,
        pointerEvents: 'none',
        outline: 'none',
        overflow: 'visible',
      }}
      filterNull={false}
    />
  ) : (
    <RechartsTooltip
      content={(props: any) => (
        <DefaultTooltip {...props} fields={tooltipFields} lines={lines} />
      )}
      allowEscapeViewBox={tooltipAllowEscape}
      position={tooltipPosition}
      offset={tooltipOffset}
      wrapperStyle={{
        zIndex: 20,
        pointerEvents: 'none',
        outline: 'none',
        overflow: 'visible',
      }}
      filterNull={false}
    />
  );

  // ---- Chart core ----
  // Do not use flexGrow here: in a column flex layout (e.g. inside Card) `flex: 1` stretches the plot to
  // fill the row and ignores `height`, so changing `height` appears to have no effect.
  const chart = (
    <Box
      sx={{
        height,
        width: '100%',
        minHeight: height,
        maxHeight: height,
        flexShrink: 0,
        position: 'relative',
        /** `hidden` clips Recharts tooltips at the plot edge (bad in narrow Grid columns). */
        overflow: 'visible',
      }}
    >
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={data} margin={margin}>
          <CartesianGrid strokeDasharray="3 3" stroke={alpha(SEARCE_COLORS.neutral_300, 0.3)} />

          <XAxis
            dataKey={xAxisDataKey}
            tick={{ fontSize: 10, fill: SEARCE_COLORS.textSecondary }}
            axisLine={{ stroke: SEARCE_COLORS.border }}
            tickLine={false}
            angle={xAxisAngle}
            textAnchor={textAnchor}
            height={resolvedXAxisHeight}
            tickFormatter={xAxisTickFormatter}
            interval={xAxisInterval}
            label={
              xAxisLabel
                ? { value: xAxisLabel, position: 'insideBottom', offset: -5, style: { fontSize: 11, fill: SEARCE_COLORS.textSecondary } }
                : undefined
            }
          />

          <YAxis
            tick={{ fontSize: 11, fill: SEARCE_COLORS.textSecondary }}
            axisLine={{ stroke: SEARCE_COLORS.border }}
            tickLine={false}
            width={yAxisWidth}
            domain={yAxisDomain}
            tickFormatter={yAxisTickFormatter}
            label={
              yAxisLabel
                ? { value: yAxisLabel, angle: -90, position: 'insideLeft', style: { fontSize: 11, fill: SEARCE_COLORS.textSecondary, textAnchor: 'middle' } }
                : undefined
            }
          />

          {tooltipContent}

          {showLegend && (
            <Legend
              wrapperStyle={{ fontSize: '0.75rem', fontWeight: 500, paddingTop: '8px' }}
              iconType="line"
              iconSize={12}
            />
          )}

          {/* Reference lines */}
          {referenceLines?.map((rl, idx) => (
            <ReferenceLine
              key={`ref-${idx}`}
              y={rl.y}
              x={rl.x}
              stroke={rl.color ?? SEARCE_COLORS.textSecondary}
              strokeDasharray={rl.strokeDasharray ?? '5 5'}
              label={
                rl.label
                  ? { value: rl.label, position: 'right', style: { fontSize: 10, fill: rl.color ?? SEARCE_COLORS.textSecondary } }
                  : undefined
              }
            />
          ))}

          {/* Extra recharts children (e.g. custom segment lines) */}
          {children}

          {/* Lines */}
          {lines.map((lineConfig) => {
            const {
              dataKey,
              label: lineName,
              color = DEFAULT_LINE_COLOR,
              getColor,
              strokeWidth = DEFAULT_STROKE_WIDTH,
              strokeDasharray,
              dotRadius = DEFAULT_DOT_RADIUS,
              activeDotRadius = DEFAULT_ACTIVE_DOT_RADIUS,
              connectNulls = false,
              showDot = true,
              shouldShowDot,
            } = lineConfig;

            const hasDynamicColor = !!getColor;

            const dotProp = !showDot
              ? false
              : hasDynamicColor
                ? renderDynamicDot(dataKey, getColor, dotRadius, shouldShowDot)
                : renderStaticDot(color, dotRadius, shouldShowDot);

            const activeDotProp = !showDot
              ? false
              : hasDynamicColor
                ? renderDynamicDot(dataKey, getColor, activeDotRadius, shouldShowDot)
                : renderStaticDot(color, activeDotRadius, shouldShowDot);

            return (
              <Line
                key={dataKey}
                type="monotone"
                dataKey={dataKey}
                name={lineName}
                stroke={hasDynamicColor ? color : color}
                strokeWidth={strokeWidth}
                strokeDasharray={strokeDasharray}
                dot={dotProp}
                activeDot={activeDotProp}
                connectNulls={connectNulls}
              />
            );
          })}
        </LineChart>
      </ResponsiveContainer>
    </Box>
  );

  // ---- Wrap in card or return bare ----
  if (!showCard) return chart;

  return (
    <Card
      sx={{
        borderRadius: 3,
        boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
        border: `1px solid ${alpha(SEARCE_COLORS.neutral_300, 0.5)}`,
        height: 'auto',
        width: '100%',
        alignSelf: 'flex-start',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        overflow: 'visible',
      }}
    >
      <CardContent
        sx={{
          p: 2,
          flex: '0 0 auto',
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
          position: 'relative',
          overflow: 'visible',
        }}
      >
        {title && (
          <Typography
            variant="h6"
            sx={{ fontWeight: 600, color: titleColor, mb: 2, fontSize: '1rem' }}
          >
            {title}
          </Typography>
        )}
        {chart}
      </CardContent>
    </Card>
  );
};

export default CustomLineChart;
